import { cn } from '@/lib/utils';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { Macro } from '@chiroup/core/types/Macro';
import React, { ChangeEvent, ReactNode, useImperativeHandle, useState } from 'react';
import { TrivialTooltip } from '../TrivialTooltip';
import { ErrorIcon } from './ErrorIcon';
import { FieldErrors } from './FieldErrors';
import MacrosPopover from './MacrosPopover';
import useMacros from './useMacros';

type Props = {
  name?: string;
  className?: string;
  icon?: ReactNode;
  label?: string | ReactNode;
  value?: string;
  onChange: (val: string) => void;
  onBlur?: (val: string) => void;
  errors?: FormError;
  disabled?: boolean;
  onFocus?: () => void;
  rows?: number;
  hint?: string;
  hintOnClick?: () => void;
  autoFocus?: boolean;
  placeHolder?: string;
  macros?: Partial<Macro>[];
  tooltip?: string;
  areaClassName?: string;
};

export const Textarea = ({
  ref,
  name,
  label,
  value,
  onChange,
  onBlur,
  errors,
  className = '',
  icon,
  disabled = false,
  onFocus,
  rows,
  hint,
  hintOnClick,
  autoFocus,
  placeHolder = '',
  macros,
  tooltip,
  areaClassName = '',
}: Props & {
  ref?: any;
}) => {
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => ({
    blur: () => {
      if (textAreaRef.current) {
        textAreaRef.current.blur();
      }
    },
    focus: () => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
      }
    },
    // You can expose other properties or methods as needed
  }));
  const [showPopover, setShowPopover] = useState(false);
  const { filteredMacros } = useMacros(value, macros);

  const handleMacroClick = (phrase: string, cursorPlacement?: number) => {
    if (typeof value === 'string' && typeof cursorPlacement === 'number') {
      const cutString = value.slice(0, cursorPlacement);
      const lastSlashIndex = cutString.lastIndexOf('/');

      const newStr = `${value.slice(0, lastSlashIndex)}${phrase} ${value.slice(cursorPlacement)}`;

      onChange?.(newStr);
      setShowPopover(false);

      setTimeout(() => {
        if (textAreaRef.current) {
          textAreaRef.current.selectionStart = lastSlashIndex + phrase.length + 1;
          textAreaRef.current.selectionEnd = lastSlashIndex + phrase.length + 1;
        }
      }, 0);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!macros?.length) {
      return;
    }

    const key = e.key;
    const code = e.code;
    const isSlash = code === 'Slash' || key === '/';
    const isSpace = code === 'Space' || key === ' ';

    if (isSlash) {
      // Only trigger macro popup if "/" will be at the start OR preceded by a space/newline
      const textarea = e.currentTarget;
      const caret = textarea.selectionStart ?? 0;
      const charBeforeCaret = textarea.value[caret - 1];

      if (caret === 0 || charBeforeCaret === ' ' || charBeforeCaret === '\n') {
        setShowPopover(true);
      }
    } else if (isSpace) {
      setShowPopover(false);
    }
  };

  const onChangeValue = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;

    if (macros?.length) {
      const caret = e.target.selectionStart ?? val.length;
      const charBeforeCaret = val[caret - 1];
      const charTwoBeforeCaret = val[caret - 2];

      // Only trigger macro popup if "/" is at the start OR preceded by a space/newline
      if (
        charBeforeCaret === '/' &&
        (caret === 1 || charTwoBeforeCaret === ' ' || charTwoBeforeCaret === '\n')
      ) {
        setShowPopover(true);
      } else if (charBeforeCaret === ' ' || !val.includes('/')) {
        setShowPopover(false);
      }
    }

    onChange(val);
  };

  const onBlurValue = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    onBlur?.(val);
  };

  return (
    <div className={cn(className, 'flex flex-col gap-1')}>
      {label && (
        <div>
          <label
            htmlFor={name}
            className="block text-sm font-medium leading-5 text-gray-700  dark:text-dark-gray-400"
          >
            {label}
            {tooltip && <TrivialTooltip text={tooltip} type="info" />}
          </label>
        </div>
      )}
      <div>
        <div className="relative rounded-md">
          <div className="relative flex items-stretch grow focus-within:z-10">
            {icon && (
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {icon}
              </div>
            )}
            <textarea
              id={name}
              name={name}
              data-slot="input"
              className={cn(
                // Base shadcn UI textarea styles (adapted from input)
                'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-h-[60px] w-full min-w-0 rounded-md border px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-white dark:bg-dark-gray-700',
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:border-primary-500 focus-visible:ring-primary-500',

                // Existing functionality styles
                errors
                  ? 'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-destructive text-destructive placeholder:text-destructive/50'
                  : '',
                disabled
                  ? 'text-gray-500 dark:text-dark-gray-300 bg-gray-300 dark:bg-dark-gray-500 cursor-not-allowed'
                  : '',
                icon ? 'pl-10' : '',
                areaClassName
              )}
              value={value || ''}
              onChange={onChangeValue}
              onFocus={onFocus}
              onBlur={onBlurValue}
              disabled={disabled}
              ref={textAreaRef}
              rows={rows}
              autoFocus={autoFocus}
              data-cy="text"
              placeholder={placeHolder}
              onKeyDown={onKeyDown}
            />
            {errors && <ErrorIcon />}
          </div>
        </div>
        <FieldErrors errors={errors} />
        {!!hint && (
          <p
            className={[
              'mt-2 text-sm',
              hintOnClick
                ? 'text-primary-600 font-semibold hover:text-primary-500 cursor-pointer italic'
                : 'text-gray-500 italic',
            ].join(' ')}
            id={`${name}-hint`}
            onClick={hintOnClick}
          >
            {hint}
          </p>
        )}
        {!!(showPopover && macros?.length) && (
          <MacrosPopover
            showPopover={showPopover}
            setShowPopover={setShowPopover}
            filteredMacros={filteredMacros}
            handleMacroClick={handleMacroClick}
            cursorPosition={textAreaRef.current?.selectionStart ?? 0}
            anchorRef={textAreaRef as React.RefObject<HTMLElement>}
          />
        )}
      </div>
    </div>
  );
};
